import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AlertConfig } from '../user.service';
import { OrgAlertHistoryItem } from '../patient.model';
import { PatientService } from '../patient.service';
import { AlertCampaignService } from '../alert-roster/alert-campaign.service';
import {
  ActiveCampaignGroup,
  buildCampaignTimeline,
  CampaignTimelineModel,
  countStatusesByCycleKey,
  buildCampaignProgressSummary,
  campaignHasInProgressAlerts,
  formatNoAlertWindowNote,
  maxScheduledEndForGroup,
  StatusCountEntry,
  StatusCountWithPercent,
  statusCountsWithPercent,
} from './active-campaign-card.util';
import { resolveAlertConfig } from '../../configuration/configuration.defaults';
import { MaterialModule } from '../../shared/material/material.module';
import {
  CampaignAlertConfigDialogComponent,
  CampaignAlertConfigDialogData,
} from './campaign-alert-config-dialog.component';

@Component({
  selector: 'app-active-campaign-card',
  standalone: true,
  imports: [CommonModule, RouterModule, MaterialModule],
  templateUrl: './active-campaign-card.component.html',
  styleUrl: './active-campaign-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveCampaignCardComponent implements OnInit, OnChanges {
  #campaignService = inject(AlertCampaignService);
  #patientService = inject(PatientService);
  #dialog = inject(MatDialog);

  @Input({ required: true })
  group!: ActiveCampaignGroup;

  @Input()
  fallbackAlertConfig?: AlertConfig;

  @Input()
  refreshVersion = 0;

  /** Hide “View in Alert History” when the card is already on `/alerts`. */
  @Input()
  hideHistoryLink = false;

  loading = signal(true);
  timeline = signal<CampaignTimelineModel | null>(null);
  statusCounts = signal<StatusCountEntry[]>([]);
  progressAttempt = signal('');
  progressSchedule = signal('');
  noAlertWindowNote = signal('');
  title = signal('');
  campaignConfigDialogData = signal<CampaignAlertConfigDialogData | null>(null);

  ngOnInit(): void {
    this.title.set(this.group.title);
    this.#loadCard();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshVersion'] && !changes['refreshVersion'].firstChange) {
      this.#loadCard();
    }
  }

  inProgressCount(): number {
    return this.group.rows.length;
  }

  visibleStatusSegments(): StatusCountEntry[] {
    const withCount = this.statusCounts().filter((entry) => entry.count > 0);
    if (withCount.length > 0) {
      return withCount;
    }
    const inProgress = this.statusCounts().find((entry) => entry.key === 'inProgress');
    if (inProgress && this.inProgressCount() > 0) {
      return [{ ...inProgress, count: this.inProgressCount() }];
    }
    return [];
  }

  statusPercentLabels(): StatusCountWithPercent[] {
    return statusCountsWithPercent(this.visibleStatusSegments()).filter(
      (entry) => entry.count > 0,
    );
  }

  /** Center of each status segment on the bar, with stagger row for callout labels. */
  statusLabelAnchors(): Array<StatusCountWithPercent & { centerPercent: number; row: number }> {
    let edge = 0;
    return this.statusPercentLabels().map((seg, index) => {
      const centerPercent = edge + seg.percent / 2;
      edge += seg.percent;
      return { ...seg, centerPercent, row: index + 1 };
    });
  }

  statusCalloutsMinHeight(): number {
    const anchors = this.statusLabelAnchors();
    if (!anchors.length) {
      return 0;
    }
    const maxRow = Math.max(...anchors.map((a) => a.row));
    return 10 + maxRow * 16 + 12;
  }

  scheduledSendCount(): number {
    return this.timeline()?.dots.length ?? 0;
  }

  statusTotalCount(): number {
    return this.statusCounts().reduce((sum, entry) => sum + entry.count, 0);
  }

  statusSegmentFlex(seg: StatusCountEntry): number {
    const total = this.statusTotalCount();
    if (total > 0) {
      return seg.count;
    }
    return 1;
  }

  totalAlertCount(): number {
    const fromHistory = this.statusTotalCount();
    if (fromHistory > 0) {
      return fromHistory;
    }
    if (!this.loading()) {
      return this.inProgressCount();
    }
    return 0;
  }

  #loadCard(): void {
    this.loading.set(true);
    this.#campaignService
      .getCampaign(this.group.campaignId)
      .pipe(
        take(1),
        switchMap((campaign) => {
          const config = campaign.alertConfig ?? this.fallbackAlertConfig ?? resolveAlertConfig();
          this.noAlertWindowNote.set(formatNoAlertWindowNote(config));
          const startMs = campaign.createdDate ?? earliestInitiated(this.group);
          const scheduledEnd = maxScheduledEndForGroup(this.group.rows);
          const loadedAt = Date.now();
          this.timeline.set(buildCampaignTimeline(config, startMs, scheduledEnd, loadedAt));
          this.progressSchedule.set(
            buildCampaignProgressSummary(this.group, startMs, scheduledEnd, config.retryAttempts)
              .schedule,
          );
          const campaignTitle = campaign.title?.trim() || this.group.title;
          if (campaign.title?.trim()) {
            this.title.set(campaignTitle);
          }
          this.#setCampaignConfigDialogData({
            title: campaignTitle,
            description: campaign.description,
            alertConfig: config,
            campaignStartMs: startMs,
          });
          return this.#fetchAllCampaignHistory(this.group.campaignId).pipe(
            switchMap((items) => {
              this.statusCounts.set(countStatusesByCycleKey(items));
              const locationLabel = items[0]?.callbackLocationName?.trim();
              if (locationLabel) {
                this.#setCampaignConfigDialogData({ locationLabel });
              }
              return this.#syncProgressAttempt(startMs, scheduledEnd, config.retryAttempts);
            }),
          );
        }),
        catchError(() => {
          const config = this.fallbackAlertConfig ?? resolveAlertConfig();
          this.noAlertWindowNote.set(formatNoAlertWindowNote(config));
          const startMs = earliestInitiated(this.group);
          const scheduledEnd = maxScheduledEndForGroup(this.group.rows);
          const loadedAt = Date.now();
          this.timeline.set(buildCampaignTimeline(config, startMs, scheduledEnd, loadedAt));
          this.progressSchedule.set(
            buildCampaignProgressSummary(this.group, startMs, scheduledEnd, config.retryAttempts)
              .schedule,
          );
          this.#setCampaignConfigDialogData({
            title: this.group.title,
            alertConfig: config,
            campaignStartMs: startMs,
          });
          const fallbackCounts = countStatusesByCycleKey([]);
          const inProgress = fallbackCounts.find((e) => e.key === 'inProgress');
          if (inProgress) {
            inProgress.count = this.group.rows.length;
          }
          this.statusCounts.set(fallbackCounts);
          return this.#syncProgressAttempt(startMs, scheduledEnd, config.retryAttempts);
        }),
      )
      .subscribe(() => this.loading.set(false));
  }

  #syncProgressAttempt(
    campaignStartMs: number,
    scheduledEndMs: number | undefined,
    retryAttemptsFromConfig?: number,
  ): Observable<null> {
    if (this.group.rows.length > 0) {
      const summary = buildCampaignProgressSummary(
        this.group,
        campaignStartMs,
        scheduledEndMs,
        retryAttemptsFromConfig,
      );
      this.progressAttempt.set(summary.attempt);
      return of(null);
    }

    if (!campaignHasInProgressAlerts(this.statusCounts())) {
      this.progressAttempt.set('');
      return of(null);
    }

    return this.#patientService.getActiveAlertPatientIds().pipe(
      take(1),
      switchMap((response) => {
        const campaignRows = (response.result ?? []).filter(
          (row) => row.alertCampaignId === this.group.campaignId,
        );
        const summary = buildCampaignProgressSummary(
          { ...this.group, rows: campaignRows },
          campaignStartMs,
          scheduledEndMs,
          retryAttemptsFromConfig,
        );
        this.progressAttempt.set(summary.attempt);
        return of(null);
      }),
      catchError(() => {
        this.progressAttempt.set('');
        return of(null);
      }),
    );
  }

  #fetchAllCampaignHistory(campaignId: string): Observable<OrgAlertHistoryItem[]> {
    return fetchAllHistoryPages(this.#patientService, campaignId);
  }

  #setCampaignConfigDialogData(partial: Partial<CampaignAlertConfigDialogData>): void {
    const current = this.campaignConfigDialogData();
    if (!current) {
      if (partial.title == null || partial.alertConfig == null || partial.campaignStartMs == null) {
        return;
      }
      this.campaignConfigDialogData.set({
        title: partial.title,
        description: partial.description,
        locationLabel: partial.locationLabel,
        alertConfig: partial.alertConfig,
        campaignStartMs: partial.campaignStartMs,
      });
      return;
    }
    this.campaignConfigDialogData.set({ ...current, ...partial });
  }

  openViewAlertConfiguration(): void {
    const data = this.campaignConfigDialogData();
    if (!data || this.loading()) {
      return;
    }
    this.#dialog.open(CampaignAlertConfigDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    });
  }
}

function earliestInitiated(group: ActiveCampaignGroup): number {
  return Math.min(...group.rows.map((r) => r.initiatedOn));
}

function fetchAllHistoryPages(
  patientService: PatientService,
  campaignId: string,
  nextPage?: string,
  acc: OrgAlertHistoryItem[] = [],
): Observable<OrgAlertHistoryItem[]> {
  return patientService.getOrgAlertHistory({ campaignId, pageSize: 500, nextPage }).pipe(
    switchMap((response) => {
      const merged = acc.concat(response.result ?? []);
      if (response.nextPage) {
        return fetchAllHistoryPages(patientService, campaignId, response.nextPage, merged);
      }
      return of(merged);
    }),
  );
}
